import { faExclamationTriangle, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EFlags, type TFlagsRecord } from '@feathr/blackbox';
import {
  Button,
  CardV2 as Card,
  DebouncedInput,
  Icon,
  Input,
  SaveButton,
  Toggle,
} from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useActionBar } from '@feathr/extender/state';
import type { EFlagCategory } from '@feathr/extender/styles/flags';
import {
  FLAG_CATEGORY_ORDER,
  flagCategoryMetadata,
  flagMetadata,
} from '@feathr/extender/styles/flags';
import { cssVar, enumValues } from '@feathr/hooks';
import { useAccount } from '@feathr/report_components';

import * as styles from './FlagsPage.css';

/** Groups every flag under its category, preserving FLAG_CATEGORY_ORDER. */
function groupFlagsByCategory(flags: EFlags[]): Array<[EFlagCategory, EFlags[]]> {
  return FLAG_CATEGORY_ORDER.map((category): [EFlagCategory, EFlags[]] => [
    category,
    flags.filter((flag) => flagMetadata(flag).category === category),
  ]).filter(([, categoryFlags]) => categoryFlags.length > 0);
}

/** Matches against the label, description, and raw flag key, so both audiences can find a flag. */
function matchesSearch(flag: EFlags, search: string): boolean {
  if (!search) {
    return true;
  }
  const { label, description } = flagMetadata(flag);
  const haystack = `${label} ${description} ${flag}`.toLowerCase();
  return haystack.includes(search.trim().toLowerCase());
}

function FlagsPage(): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { setRightActions } = useActionBar();
  const flags = account.get('flags', {} as TFlagsRecord);
  const initialFlags = useRef(flags);
  const allFlags = enumValues(EFlags) as EFlags[];
  const [changedFlags, setChangedFlags] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const visibleGroups = useMemo(
    () => groupFlagsByCategory(allFlags.filter((flag) => matchesSearch(flag, search))),
    [allFlags, search],
  );

  const matchCount = useMemo(
    () => visibleGroups.reduce((total, [, categoryFlags]) => total + categoryFlags.length, 0),
    [visibleGroups],
  );

  useEffect(() => {
    setRightActions(
      <SaveButton
        disabled={!account.isDirty}
        method={'patch'}
        model={account}
        onSave={(): void => {
          const changedFlagKeys = Object.keys(flags).filter(
            (flag) => !(flag in initialFlags.current) || flags[flag] !== initialFlags.current[flag],
          );
          setChangedFlags(changedFlagKeys);
        }}
      />,
    );
  }, [account.isDirty]);

  function handleSearchChange(value?: string): void {
    setSearch(value ?? '');
  }

  function handleToggle(flag: EFlags, value: boolean): void {
    const currentFlags = account.get('flags', {} as TFlagsRecord);
    account.set({ flags: { ...currentFlags, [flag]: value } });
  }

  function renderToggle(flag: EFlags): JSX.Element {
    const { label, description } = flagMetadata(flag);
    return (
      <Toggle
        helpText={description}
        key={flag}
        label={
          <>
            {label}
            {changedFlags.includes(flag) && (
              <Button
                className={styles.warning}
                tooltip={t('Changes will not be visible until you refresh the page')}
                type={'icon-small'}
              >
                <Icon color={cssVar('--color-warning')} icon={faExclamationTriangle} />
              </Button>
            )}
          </>
        }
        layout={'well'}
        onChange={(): void => handleToggle(flag, !flags[flag])}
        value={flags[flag]}
      />
    );
  }

  function renderCategory([category, categoryFlags]: [EFlagCategory, EFlags[]]): JSX.Element {
    const { label, description } = flagCategoryMetadata(category);
    const enabledCount = categoryFlags.filter((flag) => flags[flag]).length;
    return (
      <Card key={category}>
        <Card.Header
          description={description}
          isCollapsible={true}
          title={label}
          titleSuffix={
            <span className={styles.count}>
              {t('{{enabledCount}} of {{total}} on', {
                enabledCount,
                total: categoryFlags.length,
              })}
            </span>
          }
        />
        <Card.Content addVerticalGap={true}>{categoryFlags.map(renderToggle)}</Card.Content>
      </Card>
    );
  }

  return (
    <Page title={t('Flags')}>
      <Card>
        <Card.Header
          description={t(
            'Toggle hidden or experimental features and early-access capabilities for this account. Some changes require a page refresh to take effect.',
          )}
          title={t('Feature Flags')}
        />
        <Card.Content>
          <DebouncedInput<string> defaultValue={search} onChange={handleSearchChange}>
            {(liveValue, onChangeLiveValue): JSX.Element => (
              <Input
                aria-label={t('Search flags')}
                isClearable={true}
                name={'flag-search'}
                onChange={onChangeLiveValue}
                placeholder={t('Search flags by name or description')}
                prefix={<FontAwesomeIcon icon={faSearch} />}
                type={'text'}
                value={liveValue}
              />
            )}
          </DebouncedInput>
        </Card.Content>
      </Card>

      {visibleGroups.map(renderCategory)}

      {matchCount === 0 && (
        <Card>
          <Card.Content>{t('No flags match "{{search}}".', { search })}</Card.Content>
        </Card>
      )}
    </Page>
  );
}

export default observer(FlagsPage);
